/**
 * Module: web/src/lib/coordinates.ts
 * Author: Salvo Praxis (w/ AI pair programming partner Claude Code by Anthropic)
 * Version: 1.0.1
 * Date: 2026-10-30
 *
 * v1.0.1 (exact-coordinates review fixes): isCoordinateText, the coordinate-text rule the API's is_coordinate_text shares; readCoordFields and readAddressCoords / addressStep, the coordinate fields' commit verdict and the address box's decisions, moved here so the Node test covers them (the box routes a pair found inside other text only when it is a US point or one tap from it, and half-typed coordinates explain instead of being looked up); typed degrees and decimal minutes ("30\u00B0 01.844", "30 01.844", "W 97 46.260") are refused by name and a space-separated pair in one field needs decimals on both numbers; findCoordPair skips a road's direction letter ("I-35 S") and prefers an in-range continental-US pair; destination=, coordinate= and cp= links (and any in-range decimal pair inside a link) read, and the link message reads "Couldn't read coordinates from that link."; the transcript highlight also joins one side of a stereo call; whitespace runs collapse before matching (no slow backtracking on a long paste); comments state the drafter's rules in words instead of pointing at the drafting script's helpers; the shared rule reads a hemisphere letter only as a whole word and never starts a degrees form inside a number, and degrees and decimal minutes need two minute digits from 00 to 59 and two or more decimals, so road shorthand ("FM 150 1.5 N of Kyle") is an address, while the paste scan's lead-letter check reads a bounded window (linear on a long paste).
 *
 * Exact pickup coordinates -- the ONE shared, pure vocabulary + parser for
 * every surface that shows or edits a pickup latitude / longitude (contracts
 * assisted-entry-api v1.6.0, entry-log-api v1.0.3, mo-report-api v1.0.6):
 * the entry wizard and the draft review page (through MapEntry ->
 * CoordinateInputs), the Entry Detail modal, and the draft page's transcript
 * highlight.
 *
 *   - vocabulary: EXACT_COORDINATES ('coordinates' -- the stored precision of
 *     a typed, pasted or spoken point), GEOCODER_TIERS, precisionLabel() --
 *     ONE label set app-wide ("Exact coordinates", "Rooftop match", ...,
 *     "Hand-placed pin" for 'manual')
 *   - numbers: 7-decimal rounding (the NUMERIC(10,7) columns), display
 *     without trailing zeros or exponent notation, the range and
 *     continental-US checks, the magnitude fix (suggestFix: US latitude and
 *     longitude magnitudes never overlap, so a lost minus or a swapped order
 *     is recoverable; the drafter applies the same rule to a spoken pair from
 *     contract assisted-entry-api v1.6.2), and the soft note for committed
 *     exact coordinates
 *   - text: normalizeCoordText / parseCoordText (decimal degrees, minus
 *     look-alikes, the degree sign, N/S/E/W letters, map links; typed
 *     degrees and minutes refused by name), readCoordFields (the coordinate
 *     fields' commit verdict), findCoordPair (the pair inside pasted text),
 *     isCoordStart (so the address box never sends half-typed coordinates to
 *     the geocoder), isCoordinateText (the coordinate-text rule the API's
 *     is_coordinate_text applies too: one rule, one set of test vectors) and
 *     readAddressCoords / addressStep (what the address box does with its
 *     text, for MapEntry)
 *   - transcript: coordUtteranceIndexes -- the utterances whose digits make
 *     up a pair: each value's first six significant digits, contiguous in the
 *     digit stream of the whole leg, of one speaker label, or of one side of
 *     a stereo call (labels sharing their leading channel digit); the drafter
 *     keeps a spoken pair by the same digit rule from contract
 *     assisted-entry-api v1.6.2
 *
 * Deliberately IMPORT-FREE and erasable-only TypeScript (no enums, no
 * namespaces, and no regex lookbehind -- iOS Safari before 16.4 cannot even
 * parse one): Node 24 runs this file directly for
 * web/scripts/coordinates.test.mjs, and the SPA bundles it unchanged. The
 * parser never range-checks (a link's fallback scan keeps only an in-range
 * pair); callers do, so a range error can offer the one-tap fix. Whitespace
 * runs collapse to one space before matching (no two space quantifiers meet).
 */

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

/** Stored location precision of exact coordinates (lowercase, exact match). */
export const EXACT_COORDINATES = 'coordinates';

/** The geocoder's closed output tiers (api/operations_api/routes/geocode.py). */
export const GEOCODER_TIERS: readonly string[] = [
  'rooftop', 'range_interpolated', 'geometric_center', 'approximate',
];

/** Continental-US box: latitude 24 to 50, longitude -125 to -66 (the drafter
 *  and its judge prompt use the same box from contract assisted-entry-api
 *  v1.6.2). Latitude and longitude magnitudes never overlap. */
export const CONUS = { latMin: 24, latMax: 50, lonMin: -125, lonMax: -66 };

const LAT_MAG: readonly number[] = [24, 50];
const LON_MAG: readonly number[] = [66, 125];

/** True when the value is one of the four geocoder tiers (trimmed, any case):
 *  the server's derived-coordinates diff rule keys on exactly this set. */
export function isGeocoderTier(conf: string | null | undefined): boolean {
  if (conf === null || conf === undefined) return false;
  return GEOCODER_TIERS.includes(conf.trim().toLowerCase());
}

const PRECISION_LABELS: Record<string, string> = {
  coordinates: 'Exact coordinates',
  rooftop: 'Rooftop match',
  range_interpolated: 'Street-level',
  geometric_center: 'Area center',
  approximate: 'Approximate',
  manual: 'Hand-placed pin',
  no_match: 'No match',
};

/** Human label for a location-precision value ('' for none; an unknown
 *  value shows raw rather than being guessed at). */
export function precisionLabel(conf: string | null | undefined): string {
  if (conf === null || conf === undefined || conf === '') return '';
  return Object.prototype.hasOwnProperty.call(PRECISION_LABELS, conf)
    ? PRECISION_LABELS[conf]
    : conf;
}

// ---------------------------------------------------------------------------
// Numbers
// ---------------------------------------------------------------------------

/** 7 decimals -- the NUMERIC(10,7) scale of every pickup coordinate column. */
export function roundCoord(v: number): number {
  return Number(v.toFixed(7));
}

/** Display form: up to 7 decimals, trailing zeros and a bare trailing point
 *  dropped (30.5439430 -> "30.543943"), never exponent notation; '' for none. */
export function formatCoord(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '';
  let s = v.toFixed(7);
  if (s.includes('.')) s = s.replace(/0+$/, '').replace(/\.$/, '');
  return s === '-0' ? '0' : s;
}

/** Decimal places formatCoord(v) shows (0..7). */
export function decimalsOf(v: number): number {
  const s = formatCoord(v);
  const dot = s.indexOf('.');
  return dot < 0 ? 0 : s.length - dot - 1;
}

/** Both empty, or equal at 7 decimals. */
export function sameCoord(a: number | null | undefined, b: number | null | undefined): boolean {
  const aNone = a === null || a === undefined;
  const bNone = b === null || b === undefined;
  if (aNone || bNone) return aNone && bNone;
  return roundCoord(a as number) === roundCoord(b as number);
}

/** The server's range rule: latitude [-90, 90], longitude [-180, 180], inclusive. */
export function inRange(lat: number, lon: number): boolean {
  return Number.isFinite(lat) && Number.isFinite(lon)
    && lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180;
}

/** Inside the continental-US box, inclusive. */
export function inConus(lat: number, lon: number): boolean {
  return lat >= CONUS.latMin && lat <= CONUS.latMax
    && lon >= CONUS.lonMin && lon <= CONUS.lonMax;
}

/** A one-tap correction: the pair placed by magnitude, plus what moved. */
export interface CoordFix {
  lat: number;
  lon: number;
  /** The two numbers trade places. */
  swapped: boolean;
  /** The value that becomes the latitude carried a minus sign. */
  latSign: boolean;
  /** The value that becomes the longitude lacked its minus sign. */
  lonSign: boolean;
}

function inBand(x: number, band: readonly number[]): boolean {
  return x >= band[0] && x <= band[1];
}

/**
 * The magnitude rule: null when the pair is already inside the continental
 * US; else, when one magnitude is in [24, 50] and the other in [66, 125],
 * that pair as (latitude, minus longitude). One rule covers a missing minus,
 * a minus on the wrong number, two wrong signs, and a swapped order with or
 * without signs. Digits never change. The drafter applies the same rule to a
 * spoken pair from contract assisted-entry-api v1.6.2.
 */
export function suggestFix(lat: number, lon: number): CoordFix | null {
  if (!Number.isFinite(lat) || !Number.isFinite(lon) || inConus(lat, lon)) return null;
  const a = Math.abs(lat);
  const b = Math.abs(lon);
  if (inBand(a, LAT_MAG) && inBand(b, LON_MAG)) {
    return { lat: a, lon: -b, swapped: false, latSign: lat < 0, lonSign: lon > 0 };
  }
  if (inBand(b, LAT_MAG) && inBand(a, LON_MAG)) {
    return { lat: b, lon: -a, swapped: true, latSign: lon < 0, lonSign: lat > 0 };
  }
  return null;
}

/** Plain-language reason for a fix (or the no-fix fallback). */
export function fixHint(fix: CoordFix | null): string {
  if (!fix) return 'Check the numbers.';
  if (fix.swapped) {
    let s = 'Latitude and longitude may be swapped.';
    if (fix.lonSign) s += ' The longitude needs a minus sign.';
    if (fix.latSign) s += ' A US latitude has no minus sign.';
    return s;
  }
  if (fix.latSign && fix.lonSign) return 'The minus sign may belong to the longitude.';
  if (fix.lonSign) return 'The longitude may be missing its minus sign.';
  return 'A US latitude has no minus sign.';
}

/** The out-of-range error every coordinate surface shows. */
export const RANGE_MESSAGE =
  'Latitude must be between -90 and 90, and longitude between -180 and 180.';

/**
 * The soft note for COMMITTED exact coordinates, shown wherever the decision
 * is made (entry form, review card, approve sheet, modal Save). Never blocks;
 * a lookup result or a hand-dragged pin is never second-guessed (null unless
 * the precision is 'coordinates' and both values exist).
 */
export function exactPointNote(
  lat: number | null | undefined,
  lon: number | null | undefined,
  conf: string | null | undefined,
): string | null {
  if (conf !== EXACT_COORDINATES) return null;
  if (lat === null || lat === undefined || lon === null || lon === undefined) return null;
  if (!inConus(lat, lon)) {
    return `This point is outside the continental US. ${fixHint(suggestFix(lat, lon))}`;
  }
  const n = Math.min(decimalsOf(lat), decimalsOf(lon));
  if (n < 4) {
    const places = n === 0 ? 'No decimal places'
      : n === 1 ? 'Only 1 decimal place'
        : `Only ${n} decimal places`;
    return `${places}, so this point is rough. Ask the caller for every digit.`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Text
// ---------------------------------------------------------------------------

/** Minus look-alikes keyboards and copy-paste produce. */
const MINUS_LIKE = /[\u2212\u2010\u2011\u2012\u2013\u2014\uFE63\uFF0D]/g;
/** Non-breaking and other Unicode spaces, tabs, line breaks. */
const SPACE_LIKE = /[\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000\t\r\n]/g;

/** Character-level normalization that keeps the text's shape (no trim):
 *  safe to apply while someone is typing. */
function normalizeLoose(raw: string | null | undefined): string {
  return String(raw ?? '')
    .replace(MINUS_LIKE, '-')
    .replace(SPACE_LIKE, ' ')
    .replace(/[\u00BA\u02DA]/g, '\u00B0') // ordinal mark / ring above -> degree sign
    .replace(/\uFF0C/g, ',');
}

/** normalizeLoose, every whitespace run one space, trimmed: the form the
 *  paste scan and isCoordStart match on. */
function squash(raw: string | null | undefined): string {
  return normalizeLoose(raw).replace(/\s+/g, ' ').trim();
}

/**
 * normalizeCoordText without the trim -- for the live input buffer. Also
 * removes a space right after a decimal point (some Android keyboards turn
 * "30.5" into "30. 5").
 */
export function normalizeCoordChars(raw: string | null | undefined): string {
  return normalizeLoose(raw).replace(/(\d)\. +(?=\d)/g, '$1.');
}

/** Field text, normalized: minus look-alikes to '-', Unicode spaces to ' ',
 *  the ordinal mark to the degree sign, the Android "30. 5" space removed,
 *  every whitespace run one space, trimmed. */
export function normalizeCoordText(raw: string | null | undefined): string {
  return normalizeCoordChars(raw).replace(/\s+/g, ' ').trim();
}

export type CoordAxis = 'lat' | 'lon' | 'pair';
export type InvalidReason = 'format' | 'link' | 'axis' | 'dms';
export type CoordParse =
  | { kind: 'empty' }
  | { kind: 'one'; value: number }
  | { kind: 'pair'; lat: number; lon: number }
  | { kind: 'invalid'; reason: InvalidReason };

/** The message for each invalid reason (the coordinate fields' copy). */
export function invalidMessage(reason: InvalidReason): string {
  switch (reason) {
    case 'link': return "Couldn't read coordinates from that link. Open it and copy the numbers.";
    case 'axis': return 'N and S go with latitude, E and W with longitude.';
    case 'dms': return "Degrees and minutes can't be typed here. Use decimal degrees, like 30.030731.";
    default: return 'Use decimal degrees, like 30.030731 and -97.771006.';
  }
}

/** Text that starts like a link: a map URL, "www." or an Android geo: URI. */
const LINK_START = /^(?:https?:|www\.|geo:)/i;

const NUM = '-?\\d{1,3}(?:\\.\\d+)?';
/** Map-link shapes, most exact first: Google's place data (!3d!4d), the
 *  query-string pin (ll / q / query / sll / daddr / center, Google's
 *  destination, Apple's coordinate, with "," or %2C), Bing's cp=lat~lon, a
 *  place or search path, the viewport @lat,lon, and an Android geo: URI. */
const LINK_PATTERNS: readonly RegExp[] = [
  new RegExp(`!3d(${NUM})!4d(${NUM})`),
  new RegExp(`[?&](?:ll|q|query|sll|daddr|center|destination|coordinate)=(?:loc:)?\\+?(${NUM})(?:,|%2C)(?:\\+|%20)*(${NUM})`, 'i'),
  new RegExp(`[?&]cp=(${NUM})(?:~|%7E)(${NUM})`, 'i'),
  new RegExp(`/(?:place|search)/(${NUM}),\\+?(${NUM})`, 'i'),
  new RegExp(`@(${NUM}),(${NUM})`),
  new RegExp(`^geo:(${NUM}),(${NUM})`, 'i'),
];

function linkPair(t: string): { lat: number; lon: number } | null {
  for (const re of LINK_PATTERNS) {
    const m = re.exec(t);
    if (m) return { lat: Number(m[1]), lon: Number(m[2]) };
  }
  return null;
}

interface CoordItem {
  letter: string; // '' or one of N S E W (upper case)
  neg: boolean;
  int: string;
  frac: string | null; // digits after the decimal point; '' for "30."
  deg: boolean;
}

/** Up to four items (two degrees-and-minutes values) plus, per gap, whether
 *  only whitespace separated the neighbors. */
interface ItemRun {
  items: CoordItem[];
  /** spaced[i]: only whitespace stood between items[i] and items[i + 1]. */
  spaced: boolean[];
  /** Letter-first style ("N 30.5 W 97.7"): letters precede their numbers. */
  prefix: boolean;
}

/** Letter-first items ("N 30.5 W 97.7"). */
const PREFIX_ITEM = /^([NSEW])?\s*([+-])?\s*(\d{1,3})(?:\.(\d*))?\.?(?:\s*(\u00B0))?/i;
/** Letter-last items ("30.5 N, 97.7 W"); a letter must stand alone. */
const SUFFIX_ITEM = /^([+-])?\s*(\d{1,3})(?:\.(\d*))?\.?(?:\s*(\u00B0))?(?:\s*([NSEW])(?![A-Za-z]))?/i;
const SEPARATOR = /^\s*(?:[,;]\s*|\s+)/;

/** The items of decimal-degree shaped text, or null when it is not. The
 *  letter style is decided by the first character, so "N 30.5 W 97.7" and
 *  "30.5 N 97.7 W" both read unambiguously; mixed styles are invalid. Four
 *  items at most, so "30 32.636 N 98 27.351 W" can be named as degrees and
 *  minutes. */
function readItems(t: string): ItemRun | null {
  const prefix = /^[NSEW]/i.test(t);
  const items: CoordItem[] = [];
  const spaced: boolean[] = [];
  let rest = t;
  while (rest.length > 0) {
    if (items.length === 4) return null;
    const m = (prefix ? PREFIX_ITEM : SUFFIX_ITEM).exec(rest);
    if (!m || m[0].length === 0) return null;
    items.push(prefix
      ? { letter: (m[1] ?? '').toUpperCase(), neg: m[2] === '-', int: m[3], frac: m[4] ?? null, deg: m[5] !== undefined }
      : { letter: (m[5] ?? '').toUpperCase(), neg: m[1] === '-', int: m[2], frac: m[3] ?? null, deg: m[4] !== undefined });
    rest = rest.slice(m[0].length);
    if (rest.length === 0) break;
    const sep = SEPARATOR.exec(rest);
    if (!sep) return null;
    spaced.push(!/[,;]/.test(sep[0]));
    rest = rest.slice(sep[0].length); // a trailing separator ("30.030731,") ends the text
  }
  return items.length ? { items, spaced, prefix } : null;
}

function axisOf(letter: string): 'lat' | 'lon' | null {
  if (letter === 'N' || letter === 'S') return 'lat';
  if (letter === 'E' || letter === 'W') return 'lon';
  return null;
}

/** S and W make the value negative; a value already carrying a minus stays negative. */
function itemValue(it: CoordItem): number {
  const v = Number(it.frac !== null && it.frac !== '' ? `${it.int}.${it.frac}` : it.int);
  return it.neg || it.letter === 'S' || it.letter === 'W' ? -Math.abs(v) : v;
}

/** The number has digits after its decimal point. */
function hasDecimals(it: CoordItem): boolean {
  return it.frac !== null && it.frac.length > 0;
}

/** The address box's stricter reading: every number needs a decimal part,
 *  a degree sign or a hemisphere letter, so "35 98" is never coordinates. */
function strictItem(it: CoordItem): boolean {
  return hasDecimals(it) || it.deg || it.letter !== '';
}

/** Swap back a pair read longitude-first (E/W first, or N/S second). */
function orderPair(a: CoordItem, b: CoordItem): CoordParse {
  const ax = axisOf(a.letter);
  const bx = axisOf(b.letter);
  if (ax !== null && ax === bx) return { kind: 'invalid', reason: 'axis' };
  const first = itemValue(a);
  const second = itemValue(b);
  return ax === 'lon' || bx === 'lat'
    ? { kind: 'pair', lat: second, lon: first }
    : { kind: 'pair', lat: first, lon: second };
}

/** Whole degrees with a degree sign, then more digits ("30\u00B07",
 *  "30\u00B0 01.844"). A decimal value with a degree sign ("30.5\u00B0
 *  98.4\u00B0") is a pair, not degrees and minutes. */
const WHOLE_DEGREES_THEN_DIGIT = /(?:^|[^\d.])\d{1,3}\u00B0\s*\d/;

/**
 * Parse one coordinate field ('lat' / 'lon') or the address box ('pair').
 * Decimal degrees with an optional sign, degree sign and N/S/E/W letter; a
 * pair separated by a comma, a semicolon or spaces; a map link anywhere in
 * the text. Typed degrees-minutes-seconds are refused ('dms'), and so are
 * whole degrees followed by decimal minutes. In a field, a pair separated
 * only by spaces needs a decimal part on both numbers ("30.5439 43" is a
 * slip, not a pair). Range is NOT checked here. A field may hold a whole
 * pair (that pair wins at commit).
 */
export function parseCoordText(raw: string | null | undefined, axis: CoordAxis): CoordParse {
  const t = normalizeCoordText(raw);
  if (!t) return { kind: 'empty' };
  const link = linkPair(t);
  if (link) return { kind: 'pair', lat: link.lat, lon: link.lon };
  if (LINK_START.test(t)) {
    // A link none of the patterns knows can still carry a decimal pair
    // (a directions link's destination); a short link carries no numbers.
    const scanned = linkScan(t);
    return scanned ? { kind: 'pair', lat: scanned.lat, lon: scanned.lon } : { kind: 'invalid', reason: 'link' };
  }
  // Minute / second marks (straight, prime and smart quotes), or whole
  // degrees glued or spaced to more digits ("30\u00B07", "30\u00B0 01.844"):
  // typed DMS is named, never guessed. In the address box only
  // coordinate-shaped text can be DMS -- an apostrophe in "Love's Travel
  // Stop" is an address, not a minute mark.
  if (/['"\u2032\u2033\u2019\u201D]/.test(t) || WHOLE_DEGREES_THEN_DIGIT.test(t)) {
    return axis !== 'pair' || isCoordStart(t)
      ? { kind: 'invalid', reason: 'dms' }
      : { kind: 'invalid', reason: 'format' };
  }
  const run = readItems(t);
  if (!run) return { kind: 'invalid', reason: 'format' };
  const { items, spaced, prefix } = run;
  // Whole degrees, a space, then decimal minutes ("30 01.844", "W 97
  // 46.260", "30 01.844 N"): a GPS unit's degrees and minutes, never read as
  // a pair. The one hemisphere letter sits before the degrees or after the
  // minutes; a whole number with its own letter followed by a value with its
  // own letter ("97 W 30.5 N") is two values, not degrees and minutes, and so
  // is a signed second value (minutes are never negative).
  for (let i = 0; i + 1 < items.length; i += 1) {
    const minutes = items[i + 1];
    const ownLetter = prefix ? minutes.letter : items[i].letter;
    if (spaced[i] && !hasDecimals(items[i]) && hasDecimals(minutes) && !minutes.neg && ownLetter === '') {
      return { kind: 'invalid', reason: 'dms' };
    }
  }
  if (items.length > 2) return { kind: 'invalid', reason: 'format' };
  if (axis === 'pair' && !items.every(strictItem)) return { kind: 'invalid', reason: 'format' };
  if (items.length === 2) {
    const p = orderPair(items[0], items[1]);
    if (p.kind === 'invalid') return p; // two letters of one axis say so first
    if (axis !== 'pair' && spaced[0] && !(hasDecimals(items[0]) && hasDecimals(items[1]))) {
      return { kind: 'invalid', reason: 'format' };
    }
    return p;
  }
  const it = items[0];
  const letterAxis = axisOf(it.letter);
  if ((axis === 'lat' && letterAxis === 'lon') || (axis === 'lon' && letterAxis === 'lat')) {
    return { kind: 'invalid', reason: 'axis' };
  }
  return { kind: 'one', value: itemValue(it) };
}

/** The half-pair error of the coordinate fields. */
const BOTH_MESSAGE = 'Enter both latitude and longitude.';

/** The coordinate fields' commit verdict (range NOT checked: the caller
 *  shows RANGE_MESSAGE with the suggestFix button). */
export type FieldsRead =
  | { kind: 'empty' }
  | { kind: 'error'; message: string }
  | { kind: 'pair'; lat: number; lon: number };

/**
 * What the Latitude and Longitude fields hold, in commit order: a pair or a
 * map link in either field wins (Latitude's when both); both blank is
 * 'empty'; a field's own problem (a format, a link without numbers, a
 * wrong-axis letter, degrees and minutes) comes BEFORE "Enter both latitude
 * and longitude.", which would only send the person to the other field;
 * else the two values. Both numbers are rounded to 7 decimals.
 */
export function readCoordFields(
  latText: string | null | undefined,
  lonText: string | null | undefined,
): FieldsRead {
  const a = parseCoordText(latText, 'lat');
  const b = parseCoordText(lonText, 'lon');
  if (a.kind === 'pair') return { kind: 'pair', lat: roundCoord(a.lat), lon: roundCoord(a.lon) };
  if (b.kind === 'pair') return { kind: 'pair', lat: roundCoord(b.lat), lon: roundCoord(b.lon) };
  if (a.kind === 'empty' && b.kind === 'empty') return { kind: 'empty' };
  if (a.kind === 'invalid') return { kind: 'error', message: invalidMessage(a.reason) };
  if (b.kind === 'invalid') return { kind: 'error', message: invalidMessage(b.reason) };
  if (a.kind === 'empty' || b.kind === 'empty') return { kind: 'error', message: BOTH_MESSAGE };
  return { kind: 'pair', lat: roundCoord(a.value), lon: roundCoord(b.value) };
}

/** Two decimal numbers with optional degree signs and standalone hemisphere
 *  letters, separated by a comma, a semicolon, a slash, the word "comma" or
 *  a space, the second optionally labeled ("Long -98.4558"). Run on squashed
 *  text (one space per whitespace run), so every space quantifier is "?". */
const PAIR_SCAN = new RegExp(
  '([+-]?)(\\d{1,3}\\.\\d+) ?\u00B0?(?: ?([NSEW])(?![A-Za-z]))?'
  + '(?: ?(?:[,;/]|comma) ?| )'
  + '(?:(?:latitude|longitude|lat|lng|long|lon|gps)[:=]? ?)?'
  + '(?:([NSEW])(?![A-Za-z]) ?)?([+-]?)(\\d{1,3}\\.\\d+) ?\u00B0?(?: ?([NSEW])(?![A-Za-z]))?',
  'gi',
);

/** Text before a direction letter that names a road ("I-35 S", "US 290 W",
 *  "Hwy 71 E", "Exit 238 N", or "I-35S"): that letter is the road's
 *  direction, not a hemisphere. */
const ROAD_END = /(?:\b(?:I|IH|US|SH|HWY|FM)[ -]?\d+|\d )$/i;

/** The lead-letter check reads at most this many characters before a pair,
 *  so the scan stays linear on a long paste: a road name, its number, the
 *  letter and a space fit with room to spare (only a road number of 26 or
 *  more digits could read differently). The lead pattern's "^" needs a
 *  window of 2 characters or fewer, which only the text's own start gives. */
const LEAD_WINDOW = 32;

function scanItem(sign: string, digits: string, letter: string): CoordItem {
  const [int, frac] = digits.split('.');
  return { letter: letter.toUpperCase(), neg: sign === '-', int, frac: frac ?? null, deg: false };
}

/**
 * The decimal-pair scan over squashed text: every pair in turn (overlapping,
 * so the second number of a rejected pair can open the next one), returning
 * the first in-range pair inside the continental US, else the first in-range
 * pair, else the first pair (out of range, so the caller can offer the fix);
 * null when there is none.
 */
function scanPair(t: string): { lat: number; lon: number } | null {
  let first: { lat: number; lon: number } | null = null;
  let firstInRange: { lat: number; lon: number } | null = null;
  PAIR_SCAN.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = PAIR_SCAN.exec(t)) !== null) {
    const at = m.index;
    PAIR_SCAN.lastIndex = at + 1;
    // A match starting inside a longer number (a phone number, an IP) or
    // right after a sign that belongs to its number is not a pair start.
    if (at > 0 && /[\d.+-]/.test(t.charAt(at - 1))) continue;
    // A hemisphere letter standing alone right before the first number means
    // letter-first style ("N 30.03 W 97.77"): the letter between the numbers
    // then belongs to the second one. A road's direction letter ("I-35 S")
    // and a possessive ("Love's") are not hemispheres. Otherwise letters
    // trail their numbers.
    const before = t.slice(Math.max(0, at - LEAD_WINDOW), at);
    const lead = /(?:^|[^A-Za-z'\u2019])([NSEW]) ?$/i.exec(before);
    let leadLetter = '';
    if (lead) {
      const letterAt = before.length - (before.endsWith(' ') ? 2 : 1);
      if (!ROAD_END.test(before.slice(0, letterAt))) leadLetter = lead[1];
    }
    let firstLetter: string;
    let secondLetter: string;
    if (leadLetter && m[7] === undefined) {
      firstLetter = leadLetter;
      secondLetter = m[3] ?? m[4] ?? '';
    } else {
      firstLetter = m[3] ?? '';
      secondLetter = m[7] ?? m[4] ?? '';
    }
    const p = orderPair(scanItem(m[1], m[2], firstLetter), scanItem(m[5], m[6], secondLetter));
    if (p.kind !== 'pair') continue;
    const pair = { lat: p.lat, lon: p.lon };
    if (inRange(pair.lat, pair.lon)) {
      if (inConus(pair.lat, pair.lon)) return pair;
      if (firstInRange === null) firstInRange = pair;
    }
    if (first === null) first = pair;
  }
  return firstInRange ?? first;
}

/** The decimal-pair scan of a link no pattern knows: only an in-range pair
 *  counts (a link's other numbers are not coordinates). */
function linkScan(t: string): { lat: number; lon: number } | null {
  const p = scanPair(t);
  return p !== null && inRange(p.lat, p.lon) ? p : null;
}

/**
 * For PASTES (and the address box's coordinate text): a map link's pair,
 * else a pair anywhere in the text whose two numbers both carry a decimal
 * part (a texted "I'm at 30.030731, -97.771006." pastes cleanly), preferring
 * an in-range pair inside the continental US over an earlier stray one
 * ("Quote 125.00, 30.030731, -97.771006"); in a link no pattern knows, only
 * an in-range pair; null when there is none.
 */
export function findCoordPair(text: string | null | undefined): { lat: number; lon: number } | null {
  const t = squash(text);
  if (!t) return null;
  const link = linkPair(t);
  if (link) return link;
  return LINK_START.test(t) ? linkScan(t) : scanPair(t);
}

/**
 * True when the text starts like a decimal coordinate (an optional N/S
 * letter or sign, 1 to 3 digits, then a decimal point followed by digits or
 * the end, or a degree sign) or like a link. Plain numbers such as ZIP codes
 * and street numbers ("78640", "2537 TX-21") are false.
 */
export function isCoordStart(text: string | null | undefined): boolean {
  const t = squash(text);
  if (LINK_START.test(t)) return true;
  return /^(?:[NS] ?)?[+-]? ?\d{1,3}(?:\.(?:\d|$)| ?\u00B0)/i.test(t);
}

// ---------------------------------------------------------------------------
// The shared coordinate-text rule (the API's is_coordinate_text applies the
// same rule; both sides pass the same test vectors)
// ---------------------------------------------------------------------------

/** Every character Python's str.isspace() counts as whitespace, so both
 *  sides collapse the same runs (JS \s differs at U+001C-U+001F, U+0085 and
 *  U+FEFF). */
const RULE_SPACE = /[\t\n\x0B\f\r\x1C-\x1F\x20\x85\xA0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000]+/g;
/** The rule reads at most this many characters (code points, like Python). */
const RULE_MAX_CHARS = 200;

/** The rule's own normalization: minus look-alikes to '-', the ordinal mark
 *  to the degree sign, every whitespace run one space, trimmed, then the
 *  first 200 characters. */
function ruleText(text: unknown): string {
  if (typeof text !== 'string') return '';
  const t = text
    .replace(MINUS_LIKE, '-')
    .replace(/\u00BA/g, '\u00B0')
    .replace(RULE_SPACE, ' ')
    .replace(/^ | $/g, '');
  if (t.length <= RULE_MAX_CHARS) return t;
  let out = '';
  let n = 0;
  for (const ch of t) {
    if (n === RULE_MAX_CHARS) break;
    out += ch;
    n += 1;
  }
  return out;
}

/** Rule 3: the WHOLE text is one coordinate value ("30.543943",
 *  "-98.455848,", "30.5439N"). */
const RULE_ALONE = /^[+-]?\d{1,3}\.\d+\u00B0?[NSEW]?[.,]?$/i;

/** A hemisphere letter after its value, as a whole word: no letter follows it. */
const RULE_HEMI_AFTER = '(?: ?[NSEW](?![A-Za-z]))';

/** Rule 4 item, qualifying forms only: an optional label, then a decimal
 *  value with a hemisphere letter before it, or with a degree sign or a
 *  hemisphere letter after it, or with three or more decimals. A hemisphere
 *  letter is a whole word: a letter after the value is never followed by a
 *  letter, and a letter before it never follows one, so a label running
 *  straight into a letter ("latN 30.5") is no label. No lookbehind: the
 *  scan's start check and RULE_SEP hold the leading-letter condition too. */
const RULE_ITEM = '(?:(?:latitude|longitude|lat|lng|long|lon|gps)(?:[:=] ?| |(?![A-Za-z])))?(?:'
  + '[NSEW] ?[+-]?\\d{1,3}\\.\\d+\u00B0?' + RULE_HEMI_AFTER + '?'
  + '|[+-]?\\d{1,3}\\.\\d+(?:\u00B0' + RULE_HEMI_AFTER + '?|' + RULE_HEMI_AFTER + ')'
  + '|[+-]?\\d{1,3}\\.\\d{3,}\u00B0?' + RULE_HEMI_AFTER + '?'
  + ')';

/** Rule 4 separator: a comma, semicolon or slash, or the word "comma" (an
 *  optional space on either side), or one space. "comma" running straight
 *  into a hemisphere letter ("commaN 98.4") does not separate: that letter
 *  would follow a letter. */
const RULE_SEP = '(?: ?(?:[,;/] ?|comma(?: |(?![NSEW])))| )';

/** Rule 4 at one position (sticky): ITEM SEP ITEM, the second item not
 *  running into a digit and not followed by a distance unit ("1.2 mi"). */
const RULE_PAIR = new RegExp(
  `${RULE_ITEM}${RULE_SEP}${RULE_ITEM}`
  + '(?!\\d)(?! ?(?:mi|miles?|km|kilometers?|ft|feet|foot|yd|yards?|m)\\b)',
  'iy',
);

/** Rule 5: degrees forms whose 1 to 3 degrees digits never start right after
 *  a digit or a point ("(?:^|[^\d.])" stands in for a lookbehind): a degree
 *  sign, an optional space and a digit (30\u00B032'38.2"N, 30\u00B0 01.844),
 *  or degrees and decimal minutes the way GPS units print them: a space, two
 *  minute digits from 00 to 59, a point, two or more decimals, then a
 *  whole-word hemisphere letter (30 32.636 N). Road shorthand is not
 *  degrees: "FM 150 1.5 N" has one minute digit, "FM 150 75.25 N" has 75
 *  minutes, and "MM 227.5 1.5 N" would start inside a decimal number. */
const RULE_DEGREES: readonly RegExp[] = [
  /(?:^|[^\d.])\d{1,3}\u00B0 ?\d/,
  /(?:^|[^\d.])\d{1,3} [0-5]\d\.\d{2,} ?[NSEW](?![A-Za-z])/i,
];

/** One magnitude at most 90 and the other at most 180, either order. */
function magnitudesFit(pairText: string): boolean {
  const nums = pairText.match(/\d+\.\d+/g);
  if (nums === null || nums.length < 2) return false;
  const a = Number(nums[0]);
  const b = Number(nums[1]);
  return (a <= 90 && b <= 180) || (a <= 180 && b <= 90);
}

/**
 * The coordinate-text rule, shared with the API's is_coordinate_text: true
 * when the text is a link (contains "://" or starts with "http"), is wholly
 * one coordinate value, holds a qualifying pair anywhere (two decimal values,
 * each with three or more decimals or with a whole-word hemisphere letter or
 * a degree sign, separated by a comma, semicolon, slash, the word "comma" or
 * a space, the magnitudes a latitude and a longitude, and no distance unit
 * right after), or holds a degrees form. Such text is never forward-geocoded:
 * not by the server at approval, not by the address box. Street numbers, ZIP
 * codes, highway numbers, mile markers ("I-35 MM 227.5, 1.5 miles north")
 * and road shorthand ("FM 150 1.5 N of Kyle") are not coordinate text.
 * Pure; never throws; linear in the text.
 */
export function isCoordinateText(text: string | null | undefined): boolean {
  const t = ruleText(text);
  if (!t) return false;
  if (t.includes('://') || /^http/i.test(t)) return true;
  if (RULE_ALONE.test(t)) return true;
  if (RULE_DEGREES.some((re) => re.test(t))) return true;
  for (let at = 0; at < t.length; at += 1) {
    // An item never starts inside a number, and a leading hemisphere letter
    // never follows a letter (the "e" ending "mile" is not East).
    const prev = t.charAt(at - 1);
    if (/[\d.]/.test(prev)) continue;
    if (/[A-Za-z]/.test(prev) && /[NSEW]/i.test(t.charAt(at))) continue;
    RULE_PAIR.lastIndex = at;
    const m = RULE_PAIR.exec(t);
    if (m !== null && magnitudesFit(m[0])) return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// The address box (MapEntry): what its text is, and what to do with it
// ---------------------------------------------------------------------------

/**
 * What the address box holds:
 *   - 'address': neither coordinate text nor coordinate-shaped (looked up)
 *   - 'pair': the whole text is a pair, or coordinate text holds a pair
 *     inside the continental US or one tap from it (suggestFix)
 *   - 'one': one number, "30." still being typed included
 *   - 'invalid': a named problem (degrees and minutes, a link with no
 *     coordinates, two letters of one axis)
 *   - 'partial': starts like a coordinate, holds no word and cannot be read
 *     yet ("30.030731, -97" mid-reading)
 *   - 'held': starts like a number but reads as an address ("30.5 miles south
 *     of Kyle"; not coordinate text, so the server looks it up at approval)
 *   - 'unread': coordinate text by the shared rule that does not start like
 *     a coordinate and holds nothing the box can use (a pair far from the US,
 *     a short link inside a sentence, or an address the rule misreads, such
 *     as a stray pair in "Bay 12.505, 3.250 by the gate"), with the reason
 *     Enter names
 */
export type AddressCoords =
  | { kind: 'empty' }
  | { kind: 'address' }
  | { kind: 'held' }
  | { kind: 'partial' }
  | { kind: 'pair'; lat: number; lon: number }
  | { kind: 'one' }
  | { kind: 'invalid'; reason: InvalidReason }
  | { kind: 'unread'; reason: InvalidReason };

/** A word of two or more letters: an address, not coordinates mid-typing. */
const ADDRESS_WORD = /[A-Za-z]{2}/;

/** Typed degrees for the 'unread' message: whole degrees, a degree sign and
 *  a digit, or whole degrees and decimal minutes with a whole-word
 *  hemisphere letter, neither starting inside a decimal number. Only picks
 *  the message; it reads the minutes more loosely than the shared rule (one
 *  or two digits, any decimals), so every degrees form the rule finds is
 *  named. */
const TYPED_DEGREES = /(?:^|[^\d.])\d{1,3}(?:\u00B0 ?\d| \d{1,2}\.\d+ ?[NSEW](?![A-Za-z]))/i;

/** A pair found inside other text counts only inside the continental US or
 *  one tap from it (suggestFix), so a stray pair in an address ("Bay
 *  12.505, 3.250 by the gate") never becomes exact coordinates. */
function usShaped(p: { lat: number; lon: number }): boolean {
  return inConus(p.lat, p.lon) || suggestFix(p.lat, p.lon) !== null;
}

/** Read the address box (see AddressCoords): a plain address, or coordinate
 *  text (anything isCoordStart or isCoordinateText) as a pair, one number, a
 *  named problem, half-typed coordinates, an address that starts with a
 *  number, or coordinate text the box cannot use. */
export function readAddressCoords(text: string | null | undefined): AddressCoords {
  const t = normalizeCoordText(text);
  if (!t) return { kind: 'empty' };
  const coordText = isCoordinateText(t);
  const start = isCoordStart(t);
  if (!coordText && !start) return { kind: 'address' };
  const p = parseCoordText(t, 'pair');
  if (p.kind === 'pair') return p; // the whole text: out of range too, so the message names it
  if (coordText) {
    const found = findCoordPair(t);
    if (found !== null && usShaped(found)) return { kind: 'pair', lat: found.lat, lon: found.lon };
  }
  if (p.kind === 'one') return { kind: 'one' };
  if (p.kind === 'invalid' && p.reason !== 'format') return p;
  // A lone number still being typed ("30."): one number, never an address.
  if (parseCoordText(t, 'lat').kind === 'one') return { kind: 'one' };
  if (start) return !coordText && ADDRESS_WORD.test(t) ? { kind: 'held' } : { kind: 'partial' };
  const reason: InvalidReason = t.includes('://') ? 'link' : TYPED_DEGREES.test(t) ? 'dms' : 'format';
  return { kind: 'unread', reason };
}

/** When MapEntry reads its box: the 800 ms pause, Enter or the search button,
 *  a paste, a parent's flush() before Next / Submit / Approve, an unmount. */
export type AddressMode = 'debounce' | 'search' | 'paste' | 'flush' | 'unmount';

/** What MapEntry does: nothing, route a pair (rounded, in range) to the
 *  coordinate fields, show a message under the box (flush stops on it), or
 *  look the address up. */
export type AddressStep =
  | { kind: 'none' }
  | { kind: 'route'; lat: number; lon: number }
  | { kind: 'say'; message: string }
  | { kind: 'lookup' };

/** The address box's one-number message (a half-typed pair). */
const ONE_NUMBER_MESSAGE =
  'Type both numbers, latitude then longitude, or use the Latitude and Longitude boxes.';

/**
 * The address box's decision table. Coordinate text is never looked up. A
 * pair routes when in range; out of range it explains (a paste and an
 * unmount stay quiet: the pause explains a paste, and an unmount has no one
 * to tell). One number and half-typed coordinates wait during a pause and a
 * paste, and explain on Enter and on flush. A named problem (degrees and
 * minutes, a link with no coordinates, two letters of one axis) explains,
 * except on a paste and an unmount. Coordinate text the box cannot use that
 * does not start like a coordinate explains on Enter only (it may be an
 * address the rule misreads, so it never blocks flush). An address
 * is looked up on Enter and after a pause of 4 or more characters; an
 * address that starts with a number is looked up on Enter only.
 */
export function addressStep(text: string | null | undefined, mode: AddressMode): AddressStep {
  const read = readAddressCoords(text);
  switch (read.kind) {
    case 'pair': {
      const lat = roundCoord(read.lat);
      const lon = roundCoord(read.lon);
      if (inRange(lat, lon)) return { kind: 'route', lat, lon };
      if (mode === 'paste' || mode === 'unmount') return { kind: 'none' };
      const fix = suggestFix(lat, lon);
      return { kind: 'say', message: fix ? `${RANGE_MESSAGE} ${fixHint(fix)}` : RANGE_MESSAGE };
    }
    case 'one':
      return mode === 'search' || mode === 'flush'
        ? { kind: 'say', message: ONE_NUMBER_MESSAGE }
        : { kind: 'none' };
    case 'partial':
      return mode === 'search' || mode === 'flush'
        ? { kind: 'say', message: invalidMessage('format') }
        : { kind: 'none' };
    case 'invalid':
      return mode === 'paste' || mode === 'unmount'
        ? { kind: 'none' }
        : { kind: 'say', message: invalidMessage(read.reason) };
    case 'unread':
      return mode === 'search' ? { kind: 'say', message: invalidMessage(read.reason) } : { kind: 'none' };
    case 'address':
      if (mode === 'search') return { kind: 'lookup' };
      return mode === 'debounce' && String(text ?? '').trim().length >= 4
        ? { kind: 'lookup' }
        : { kind: 'none' };
    case 'held':
      return mode === 'search' ? { kind: 'lookup' } : { kind: 'none' };
    default:
      return { kind: 'none' };
  }
}

// ---------------------------------------------------------------------------
// Transcript: where a pair was read out (draft review highlight)
// ---------------------------------------------------------------------------

/** The utterance fields the highlight needs (DraftUtterance satisfies it). */
export interface DigitUtterance {
  speaker?: string | null;
  text?: string | null;
}

interface DigitStream {
  digits: string;
  /** owner[k] = the utterance index digit k came from. */
  owner: number[];
}

/** Significant digits of |v| as formatCoord shows it: no point, no leading zeros. */
function significantDigits(v: number): string {
  return formatCoord(Math.abs(v)).replace('.', '').replace(/^0+/, '');
}

/** The match key: the first six significant digits of |v| read from its
 *  7-place STORED form, not the trimmed display form, so a pair spoken as
 *  "30.5400" (served as 30.54) still matches. The drafter keys a spoken pair
 *  the same way from contract assisted-entry-api v1.6.2. */
function matchKey(v: number): string {
  return Math.abs(v).toFixed(7).replace('.', '').replace(/^0+/, '').slice(0, 6);
}

/** The stream for one key, created on first use. */
function streamFor(streams: Map<string, DigitStream>, key: string): DigitStream {
  let s = streams.get(key);
  if (!s) {
    s = { digits: '', owner: [] };
    streams.set(key, s);
  }
  return s;
}

/**
 * Indexes (ascending) of the utterances whose digits make up the pair: each
 * value's first six significant digits of its 7-place stored form
 * (matchKey), contiguous in the ASCII-digit stream of the whole leg, of one
 * speaker label, or of one side of a stereo call (the labels whose first
 * character is the same channel digit, such as "1A" and "1B"), so a number
 * split across utterances, interrupted by the other side, or relabeled
 * within one side still matches. The value's full displayed digits are
 * matched too, so a reading that runs past six digits highlights every line
 * it spans. Every occurrence counts (the caller's reading AND a read-back).
 * Empty when either value is missing. The drafter keeps a spoken pair by the
 * same digit rule from contract assisted-entry-api v1.6.2.
 */
export function coordUtteranceIndexes(
  utterances: readonly DigitUtterance[] | null | undefined,
  lat: number | null | undefined,
  lon: number | null | undefined,
): number[] {
  if (!utterances || utterances.length === 0) return [];
  if (lat === null || lat === undefined || lon === null || lon === undefined) return [];
  const whole: DigitStream = { digits: '', owner: [] };
  const bySpeaker = new Map<string, DigitStream>();
  const bySide = new Map<string, DigitStream>();
  utterances.forEach((u, i) => {
    if (!u || typeof u !== 'object') return;
    const ds = String(u.text ?? '').replace(/[^0-9]/g, '');
    if (!ds) return;
    const label = String(u.speaker ?? '');
    const targets = [whole, streamFor(bySpeaker, label)];
    if (/^[0-9]/.test(label)) targets.push(streamFor(bySide, label.charAt(0)));
    for (const ch of ds) {
      for (const s of targets) {
        s.digits += ch;
        s.owner.push(i);
      }
    }
  });
  const streams: DigitStream[] = [whole, ...bySpeaker.values(), ...bySide.values()];
  const hits = new Set<number>();
  for (const v of [lat, lon]) {
    const full = significantDigits(v);
    const key = matchKey(v);
    if (key.length < 6) return []; // |v| below 0.00001 has no six-digit key: no match
    const needles = full.length > 6 ? [full, key] : [key];
    for (const needle of needles) {
      for (const s of streams) {
        let from = 0;
        for (;;) {
          const at = s.digits.indexOf(needle, from);
          if (at < 0) break;
          for (let k = at; k < at + needle.length; k++) hits.add(s.owner[k]);
          from = at + needle.length;
        }
      }
    }
  }
  return [...hits].sort((a, b) => a - b);
}
